import React, { useCallback, useEffect, useState } from "react";

import {
  createModbusDevice,
  deleteModbusDevice,
  getModbusDeviceState,
  getModbusRuntimeStatus,
  listModbusDevices,
  listModbusSerialPorts,
  reconnectModbusDevice,
  updateModbusDevice,
} from "../../../../api";
import plusIcon from "../../../../assets/plus.svg";
import moreIcon from "../../../../assets/more-horiz.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import NudgeNumber from "../../../../components/NudgeNumber";
import Select from "../../../../components/Select";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import {
  buildModbusDevicePayload,
  deriveModbusDeviceStatus,
  groupModbusRTUBuses,
  modbusDeviceFormFromRecord,
  validateModbusDeviceForm,
  validateModbusRTUBusAssignment,
} from "./modbus-device-model";
import {
  EdgeRuntimeConfigurationLink,
  driverRuntimeAllowsDeviceCreation,
} from "./driver-runtime-gate";

const POLL_INTERVAL_MS = 10000;
const STATUS_STYLES = {
  online: {
    background: "rgba(76,175,80,0.12)",
    color: "rgba(76,175,80,0.95)",
    border: "rgba(76,175,80,0.25)",
    dot: "#4caf50",
  },
  offline: {
    background: "rgba(158,158,158,0.12)",
    color: "var(--gw-text-secondary)",
    border: "rgba(158,158,158,0.25)",
    dot: "#9e9e9e",
  },
  degraded: {
    background: "rgba(255,152,0,0.12)",
    color: "rgba(255,167,38,0.95)",
    border: "rgba(255,152,0,0.25)",
    dot: "#ff9800",
  },
  disabled: {
    background: "rgba(158,158,158,0.08)",
    color: "var(--gw-muted)",
    border: "var(--gw-border)",
    dot: "#737373",
  },
  checking: {
    background: "rgba(255,193,7,0.12)",
    color: "rgba(255,193,7,0.95)",
    border: "rgba(255,193,7,0.25)",
    dot: "#ffc107",
  },
  unavailable: {
    background: "rgba(244,67,54,0.12)",
    color: "rgba(244,67,54,0.95)",
    border: "rgba(244,67,54,0.25)",
    dot: "#f44336",
  },
};

const errorMessage = (error, fallback) => {
  const message = String(error?.message || error || "").trim();
  return message || fallback;
};

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const StatusBadge = ({ status }) => {
  const colors = STATUS_STYLES[status.key] || STATUS_STYLES.unavailable;
  return (
    <span
      title={status.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        borderRadius: 999,
        border: `1px solid ${colors.border}`,
        background: colors.background,
        color: colors.color,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: colors.dot }} />
      {status.label}
    </span>
  );
};

const NumberField = ({ label, value, onChange, min, max, hint, list }) => (
  <div className="gwField">
    <label className="gwLabel">{label}</label>
    <NudgeNumber
      value={value === "" || value === null || value === undefined ? null : Number(value)}
      onChange={(next) => onChange(next === null ? "" : String(next))}
      min={min === undefined ? undefined : Number(min)}
      max={max === undefined ? undefined : Number(max)}
      list={list}
      aria-label={label}
    />
    {hint && <div className="gwHint">{hint}</div>}
  </div>
);

const DeviceModal = ({
  editing,
  devices = [],
  onClose,
  onSaved,
  runtimeAvailable = true,
}) => {
  const [form, setForm] = useState(() => modbusDeviceFormFromRecord(editing));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [serialPorts, setSerialPorts] = useState([]);
  const [serialPortsAvailable, setSerialPortsAvailable] = useState(false);
  const notify = useNotify();
  const isEdit = !!editing;
  const isRTU = form.transport === "rtu";

  useEffect(() => {
    let cancelled = false;
    if (!runtimeAvailable) return undefined;
    listModbusSerialPorts()
      .then((result) => {
        if (cancelled) return;
        setSerialPorts(Array.isArray(result?.ports) ? result.ports : []);
        setSerialPortsAvailable(result?.available === true);
      })
      .catch(() => {
        if (!cancelled) setSerialPortsAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runtimeAvailable]);

  const setText = (field) => (event) => {
    setSubmitError("");
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const setValue = (field) => (value) => {
    setSubmitError("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setTransport = (value) => {
    const transport = value === "rtu" ? "rtu" : "tcp";
    setSubmitError("");
    setForm((current) => ({
      ...current,
      transport,
      unitId:
        transport === "rtu" &&
        (Number(current.unitId) < 1 || Number(current.unitId) > 247)
          ? "1"
          : current.unitId,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateModbusDeviceForm(form);
    const busAssignmentError = validationError
      ? ""
      : validateModbusRTUBusAssignment(form, devices, editing?.id);
    if (validationError || busAssignmentError) {
      setSubmitError(validationError || busAssignmentError);
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    const payload = buildModbusDevicePayload(form);
    try {
      if (isEdit) {
        await updateModbusDevice(editing.id, payload);
      } else {
        await createModbusDevice(payload);
      }
      notify({
        type: "success",
        title: isEdit ? "Device updated" : "Device added",
        message: `Modbus ${payload.transport.toUpperCase()} device "${
          payload.name
        }" ${isEdit ? "was updated" : "was added"}.`,
      });
      await onSaved();
      onClose();
    } catch (error) {
      const message = errorMessage(
        error,
        `Could not ${isEdit ? "update" : "add"} the device.`
      );
      setSubmitError(message);
      notify({
        type: "error",
        title: isEdit ? "Update failed" : "Create failed",
        message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={submitting ? undefined : onClose}>
      <form
        className="gwModal"
        style={{ width: 590 }}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">
              {isEdit ? `Edit ${editing.name}` : "New Modbus device"}
            </div>
            <div className="gwModalSubtitle">
              {runtimeAvailable
                ? "Configure a Control Seat Modbus TCP or serial RTU client connection."
                : "Save device configuration for a future local runtime deployment."}
            </div>
          </div>
          <button
            className="gwModalClose"
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            <img
              src={xmarkIcon}
              alt="Close"
              className="gwIcon"
              style={{ width: 10, height: 10, opacity: 0.7 }}
            />
          </button>
        </div>

        <div className="gwModalBody">
          {submitError && (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Could not save device</div>
              <div className="gwAlertBody">{submitError}</div>
            </div>
          )}

          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input
              className="gwInput"
              value={form.name}
              onChange={setText("name")}
              placeholder="Packaging PLC"
              autoFocus
              required
            />
            <div className="gwHint">
              A unique operator-facing name for this device.
            </div>
          </div>

          <div className="gwField">
            <label className="gwLabel">Transport</label>
            <Select
              value={form.transport}
              onChange={setTransport}
              options={[
                { value: "tcp", label: "TCP/IP" },
                { value: "rtu", label: "Serial RTU (RS-232 / RS-485)" },
              ]}
            />
            <div className="gwHint">
              {isRTU
                ? "Runs on the local gateway or edge runtime with the serial adapter attached."
                : "Connects over the plant network from the runtime host."}
            </div>
          </div>

          {!isRTU && (
            <div className="gwFieldRow" style={{ gridTemplateColumns: "1fr 150px" }}>
              <div className="gwField">
                <label className="gwLabel">Host</label>
                <input
                  className="gwInput"
                  value={form.host}
                  onChange={setText("host")}
                  placeholder="192.168.1.50 or plc.local"
                  required
                />
                <div className="gwHint">
                  Hostname or IP address only; do not include a URL scheme.
                </div>
              </div>
              <NumberField
                label="TCP port"
                value={form.port}
                onChange={setValue("port")}
                min="1"
                max="65535"
                hint="Default 502"
              />
            </div>
          )}

          {isRTU && (
            <>
              <div className="gwField">
                <label className="gwLabel">Serial port</label>
                <input
                  className="gwInput"
                  value={form.rtuPort}
                  onChange={setText("rtuPort")}
                  placeholder="/dev/serial/by-path/… or COM3"
                  list="modbus-serial-ports"
                  required
                />
                <datalist id="modbus-serial-ports">
                  {serialPorts.map((port) => (
                    <option value={port} key={port} />
                  ))}
                </datalist>
                <div className="gwHint">
                  {serialPortsAvailable && serialPorts.length > 0
                    ? `${serialPorts.length} port${
                        serialPorts.length === 1 ? "" : "s"
                      } detected on this runtime. Use the listed path so the same adapter remains one bus after a reboot.`
                    : "No stable serial port was detected. USB adapters must use the detected /dev/serial/by-path name; compatible slaves share one supervised bus and use unique unit IDs."}
                </div>
				<div className="gwHint">Control Seat must be the only Modbus master on this serial bus. Multiple field devices are supported when each has a unique address.</div>
              </div>
              <div className="gwFieldRow" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                <NumberField
                  label="Baud rate"
                  value={form.baudRate}
                  onChange={setValue("baudRate")}
                  min="300"
                  max="4000000"
                  list="modbus-baud-rates"
                />
                <div className="gwField">
                  <label className="gwLabel">Parity</label>
                  <Select
                    value={form.parity}
                    onChange={setValue("parity")}
                    options={[
                      { value: "even", label: "Even" },
                      { value: "odd", label: "Odd" },
                      { value: "none", label: "None" },
                    ]}
                  />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Stop bits</label>
                  <Select
                    value={form.stopBits}
                    onChange={setValue("stopBits")}
                    options={[
                      { value: "1", label: "1" },
                      { value: "2", label: "2" },
                    ]}
                  />
                </div>
              </div>
              <datalist id="modbus-baud-rates">
                {[1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200].map(
                  (rate) => (
                    <option value={rate} key={rate} />
                  )
                )}
              </datalist>
            </>
          )}

          <NumberField
            label="Unit ID"
            value={form.unitId}
            onChange={setValue("unitId")}
            max={isRTU ? "247" : "255"}
            min={isRTU ? "1" : "0"}
            hint={
              isRTU
                ? "RTU slave address from 1 to 247. Broadcast unit 0 is excluded because commands require a verified response."
                : "The Modbus unit identifier sent with each request. Unit ID 0 is valid when the TCP device supports it."
            }
          />

          <div className="gwFieldRow" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
            <NumberField
              label="Request timeout (ms)"
              value={form.timeoutMs}
              onChange={setValue("timeoutMs")}
              min="50"
              max="60000"
            />
            <NumberField
              label="Reconnect min (ms)"
              value={form.reconnectMinMs}
              onChange={setValue("reconnectMinMs")}
              min="50"
              max="60000"
            />
            <NumberField
              label="Reconnect max (ms)"
              value={form.reconnectMaxMs}
              onChange={setValue("reconnectMaxMs")}
              min="50"
              max="300000"
            />
          </div>
          <div className="gwHint">
            Reconnect min/max bound the automatic reconnect backoff after a failed request.
          </div>

          <div className="gwToggleRow">
            <div>
              <div className="gwLabel">
                {runtimeAvailable
                  ? "Enable polling and writes for this device"
                  : "Enable when this configuration is deployed to a local runtime"}
              </div>
            </div>
            <button
              type="button"
              className={`gwToggle ${form.enabled ? "isOn" : ""}`}
              role="switch"
              aria-checked={form.enabled}
              aria-label="Enable polling and writes for this device"
              onClick={() => {
                setSubmitError("");
                setForm((current) => ({ ...current, enabled: !current.enabled }));
              }}
            >
              <span />
            </button>
          </div>

          <div className="gwNotice isSoft">
            {runtimeAvailable
              ? isRTU
                ? "This slave joins one local RTU bus by serial port, line settings, timeout, and reconnect profile. Every enabled slave on that bus must match the profile and use a unique unit ID. Configure points on normal tags in the unified tag editor."
                : "This connection is a Modbus TCP client. Configure each coil or register as a Modbus source on its normal tag in the unified tag editor."
              : "Configuration only: this cloud control plane will not connect to the device, and Modbus tag bindings are blocked until an on-prem or edge runtime can execute them."}
          </div>

          {isRTU && (
            <div className="gwNotice isSoft">
              Serial configuration executes only on the runtime host with the adapter attached. Automatic cloud-to-edge connector rollout is not available yet.
            </div>
          )}
        </div>

        <div className="gwModalFooter">
          <button
            className="gwGhostBtn"
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving…" : isEdit ? "Save device" : "Add device"}
          </button>
        </div>
      </form>
    </div>
  );
};

export const ModbusDevicesPage = () => {
  const canConfigure = usePermission("configure");
  const notify = useNotify();
  const [devices, setDevices] = useState([]);
  const [states, setStates] = useState({});
  const [stateErrors, setStateErrors] = useState({});
  const [runtimeStatus, setRuntimeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reconnecting, setReconnecting] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});

  const fetchAll = useCallback(async (showErrorToast = false) => {
    setRefreshing(true);
    try {
      const [listedDevices, status] = await Promise.all([
        listModbusDevices(),
        getModbusRuntimeStatus(),
      ]);
      const list = listedDevices || [];
      setDevices(list);
      setRuntimeStatus(status);
      setLoadError("");

      if (status?.available === false) {
        setStates({});
        setStateErrors({});
        return;
      }

      const results = await Promise.all(list.map(async (device) => {
        try {
          return { id: device.id, state: await getModbusDeviceState(device.id), error: "" };
        } catch (error) {
          return {
            id: device.id,
            state: null,
            error: errorMessage(error, "Could not load live device state."),
          };
        }
      }));
      const liveIDs = new Set(list.map((device) => device.id));
      setStates((current) => {
        const next = { ...current };
        results.forEach((result) => {
          if (result.state) next[result.id] = result.state;
        });
        Object.keys(next).forEach((id) => {
          if (!liveIDs.has(id)) delete next[id];
        });
        return next;
      });
      setStateErrors(() => {
        const next = {};
        results.forEach((result) => {
          if (result.error) next[result.id] = result.error;
        });
        return next;
      });
    } catch (error) {
      const message = errorMessage(error, "Could not load Modbus devices.");
      setLoadError(message);
      if (showErrorToast) {
        notify({ type: "error", title: "Modbus devices unavailable", message });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchAll(true);
    const interval = setInterval(() => fetchAll(false), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchAll]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeMenu = (event) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (!menu?.contains(event.target)) setMenuOpen(null);
    };
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [menuOpen]);

  const openMenu = (event, id) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: `${Math.round(rect.bottom + 4)}px`,
      right: `${Math.max(12, Math.round(window.innerWidth - rect.right))}px`,
    });
    setMenuOpen((current) => current === id ? null : id);
  };

  const handleReconnect = async (device) => {
    setMenuOpen(null);
    setReconnecting(device.id);
    try {
      await reconnectModbusDevice(device.id);
      setStates((current) => ({
        ...current,
        [device.id]: { device_id: device.id, status: "offline" },
      }));
      notify({
        type: "success",
        title: device.transport === "rtu" ? "Serial connection rebuilt" : "Connection rebuilt",
        message: device.transport === "rtu"
          ? `The port ${device.rtu?.port || "for this serial bus"} was reopened. Devices on this serial bus will report online after their next successful poll.`
          : `"${device.name}" will report online after its next successful mapped-tag poll.`,
      });
      await fetchAll(false);
    } catch (error) {
      const message = errorMessage(error, "Could not reconnect the device.");
      notify({ type: "error", title: "Reconnect failed", message });
    } finally {
      setReconnecting(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteModbusDevice(deleteTarget.id);
      notify({
        type: "success",
        title: "Device removed",
        message: `Modbus ${String(deleteTarget.transport || "tcp").toUpperCase()} device "${deleteTarget.name}" was removed.`,
      });
      setDeleteTarget(null);
      await fetchAll(false);
    } catch (error) {
      const message = errorMessage(error, "Could not remove the device.");
      notify({ type: "error", title: "Remove failed", message });
    } finally {
      setDeleting(false);
    }
  };

  const hasDevices = devices.length > 0;
  const runtimeUnavailable = runtimeStatus?.available === false;
  const runtimeExecutable = driverRuntimeAllowsDeviceCreation(runtimeStatus);
  const rtuBuses = groupModbusRTUBuses(devices);

  useEffect(() => {
    if (runtimeUnavailable) setShowCreate(false);
  }, [runtimeUnavailable]);

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Modbus</div>
            <div className="gwPageSubtitle">
              {runtimeUnavailable
                ? `${devices.length} device configuration${devices.length === 1 ? "" : "s"} stored · runtime unavailable`
                : hasDevices
                ? `${devices.length} client device${devices.length === 1 ? "" : "s"} configured`
                : "Connect Control Seat to Modbus TCP and serial RTU devices."}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="gwGhostBtn" type="button" onClick={() => fetchAll(true)} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            {runtimeExecutable ? (
              <button
                className="gwPrimaryBtn"
                type="button"
                onClick={() => setShowCreate(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                Add device
              </button>
            ) : runtimeUnavailable ? <EdgeRuntimeConfigurationLink /> : null}
          </div>
        </div>

        {runtimeUnavailable && (
          <div className="gwAlert" role="status">
            <div className="gwAlertTitle">Configuration only — Modbus runtime unavailable</div>
            <div className="gwAlertBody">
              {runtimeStatus?.reason || "This control plane stores Modbus configuration but does not execute Modbus I/O."}
              {" "}Device reconnect and new tag bindings are disabled here.
            </div>
          </div>
        )}

        <div className="gwNotice isSoft">
          <strong>Tag point mappings live in the unified tag editor.</strong>{" "}
          {runtimeUnavailable
            ? "Existing mappings remain visible but Bad/unavailable; create or change mappings on the local runtime that can reach the plant network."
            : <>Choose this device when configuring a tag's Modbus source. Addresses are strict zero-based PDU addresses from 0 to 65535: use <strong>0</strong> for the first coil or register, not 00001 or 40001 reference notation.</>}
        </div>

        {loadError && (
          <div className="gwAlert" role="alert">
            <div className="gwAlertTitle">Could not load Modbus devices</div>
            <div className="gwAlertBody">{loadError}</div>
          </div>
        )}

        {loading && !hasDevices && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
            Loading devices…
          </div>
        )}

        {!loading && !hasDevices && !loadError && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No Modbus devices yet</div>
            <div className="gwEmptyBody">
              {runtimeUnavailable
                ? "Create the device on an enrolled runtime that can reach the plant network. This control plane cannot execute Modbus I/O."
                : "Add a TCP or RTU client device, then map its coils and registers on normal tags."}
            </div>
            <div className="gwEmptyActions">
              {runtimeExecutable ? (
                <button
                  className="gwPrimaryBtn"
                  type="button"
                  onClick={() => setShowCreate(true)}
                  disabled={!canConfigure}
                  title={canConfigure ? undefined : "Requires configure permission"}
                >
                  Add device
                </button>
              ) : runtimeUnavailable ? <EdgeRuntimeConfigurationLink className="gwPrimaryBtn" /> : null}
            </div>
          </div>
        )}

        {rtuBuses.length > 0 && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Serial RTU buses</div>
            <div className="gwCardSectionBody" style={{ display: "grid", gap: 10 }}>
              {rtuBuses.map((bus) => (
                <div className="gwNotice isSoft" key={bus.port} style={{ margin: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <strong style={{ overflowWrap: "anywhere" }}>{bus.port || "Serial port"}</strong>
                    <span>
                      {bus.slaves.length} slave{bus.slaves.length === 1 ? "" : "s"} configured
                    </span>
                  </div>
                  <div style={{ marginTop: 4, color: "var(--gw-text-secondary)" }}>
                    {bus.profile}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {bus.slaves.map((slave, index) => (
                      <span
                        key={`${slave.name}-${slave.unitId}-${index}`}
                        style={{
                          border: "1px solid var(--gw-border)",
                          borderRadius: 999,
                          padding: "3px 8px",
                          color: slave.enabled ? "var(--gw-text-primary)" : "var(--gw-muted)",
                        }}
                      >
                        {slave.name} · Unit {slave.unitId}{slave.enabled ? "" : " · Disabled"}
                      </span>
                    ))}
                  </div>
                  {!bus.compatible && (
                    <div style={{ marginTop: 8, color: "var(--gw-danger)" }}>
                      Saved definitions on this port have incompatible bus profiles. They cannot run together; edit or disable the conflicting slave.
                    </div>
                  )}
                </div>
              ))}
              <div className="gwHint">
                One local runtime owns each serial handle and serializes all slave requests. Cloud-to-edge serial connector rollout still requires a separate deployment step.
              </div>
            </div>
          </div>
        )}

        {hasDevices && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Client devices</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {devices.map((device) => {
                const state = states[device.id];
                const stateError = stateErrors[device.id];
                const status = runtimeUnavailable
                  ? { key: "unavailable", label: "Runtime unavailable" }
                  : deriveModbusDeviceStatus(device, state, stateError);
                const lastSuccess = formatTime(state?.last_success_at);
                const lastAttempt = formatTime(state?.last_attempt_at);
                const busGroup = device.transport === "rtu"
                  ? rtuBuses.find((bus) => bus.port === String(device.rtu?.port || "").trim())
                  : null;
                const busReconnect = formatTime(state?.rtu_bus?.next_reconnect_at);
                return (
                  <div
                    className="umUserRow"
                    key={device.id}
                    data-gw-resource="modbus_devices"
                    data-gw-id={device.id}
                    data-gw-name={device.name}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        flexShrink: 0,
                        background: "rgba(80,140,255,0.12)",
                        border: "1px solid rgba(80,140,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "rgba(140,180,255,0.95)",
                      }}
                    >
                      MB
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span className="umUserName">{device.name}</span>
                        <StatusBadge status={status} />
                      </div>
                      <div className="umUserMeta">
                        {device.transport === "rtu"
                          ? `${device.rtu?.port || "Serial port"} · ${device.rtu?.baud_rate || 19200} ${String(device.rtu?.parity || "even").slice(0, 1).toUpperCase()}8${device.rtu?.stop_bits || 1}`
                          : `${device.tcp?.host}:${device.tcp?.port}`} &middot; Unit {device.unit_id}
                        {busGroup && <> &middot; {busGroup.slaves.length > 1 ? `${busGroup.slaves.length} slaves share this bus` : "Dedicated serial bus"}</>}
                        {state?.rtu_bus?.status && <> &middot; Bus {state.rtu_bus.status}</>}
                        {busReconnect && <> &middot; Retry {busReconnect}</>}
                        {lastSuccess && <> &middot; Last success {lastSuccess}</>}
                        {!lastSuccess && lastAttempt && <> &middot; Last attempt {lastAttempt}</>}
                        {state?.consecutive_failures > 0 && (
                          <> &middot; {state.consecutive_failures} consecutive failure{state.consecutive_failures === 1 ? "" : "s"}</>
                        )}
                      </div>
                      {state?.error && (
                        <div className="umUserMeta" style={{ color: "var(--gw-danger)", marginTop: 2 }}>
                          {state.error}
                        </div>
                      )}
                      {stateError && (
                        <div className="umUserMeta" style={{ color: "var(--gw-danger)", marginTop: 2 }}>
                          Status refresh failed: {stateError}{state ? " Showing the last observed state." : ""}
                        </div>
                      )}
                      {status.key === "offline" && !state?.error && (
                        <div className="umUserMeta" style={{ marginTop: 2 }}>
                          No successful mapped-tag request is currently confirmed.
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="gwIconButton"
                      style={{ flexShrink: 0 }}
                      onClick={(event) => openMenu(event, device.id)}
                      aria-label={`Actions for ${device.name}`}
                    >
                      <img src={moreIcon} alt="" className="gwIcon" style={{ width: 16, height: 16 }} />
                    </button>
                    {menuOpen === device.id && (
                      <div className="gwProjectMenu" style={menuStyle} onClick={(event) => event.stopPropagation()}>
                        <button
                          className="gwProjectMenuItem"
                          type="button"
                          disabled={!canConfigure}
                          title={canConfigure ? undefined : "Requires configure permission"}
                          onClick={() => {
                            setMenuOpen(null);
                            setEditing(device);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="gwProjectMenuItem"
                          type="button"
                          disabled={!canConfigure || runtimeUnavailable || !device.enabled || reconnecting === device.id}
                          title={!canConfigure
                            ? "Requires configure permission"
                            : runtimeUnavailable
                              ? "Reconnect requires a local Modbus runtime"
							: !device.enabled
							  ? "Enable the device before reconnecting"
							  : device.transport === "rtu"
								? "Close and reopen the shared serial connection"
                                : "Close the current client connection and build a new one"}
                          onClick={() => handleReconnect(device)}
                        >
                          {reconnecting === device.id ? "Reconnecting…" : device.transport === "rtu" ? "Reconnect bus" : "Reconnect"}
                        </button>
                        <div className="gwProfileDivider" />
                        <button
                          className="gwProjectMenuItem gwProjectMenuDanger"
                          type="button"
                          disabled={!canConfigure}
                          title={canConfigure ? undefined : "Requires configure permission"}
                          onClick={() => {
                            setMenuOpen(null);
                            setDeleteTarget(device);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {showCreate && runtimeExecutable && (
        <DeviceModal devices={devices} runtimeAvailable onClose={() => setShowCreate(false)} onSaved={() => fetchAll(false)} />
      )}
      {editing && (
        <DeviceModal devices={devices} runtimeAvailable={!runtimeUnavailable} editing={editing} onClose={() => setEditing(null)} onSaved={() => fetchAll(false)} />
      )}

      {deleteTarget && (
        <div className="gwOverlay" onClick={deleting ? undefined : () => setDeleteTarget(null)}>
          <div className="gwModal" style={{ width: 440 }} onClick={(event) => event.stopPropagation()}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Modbus device</div>
                <div className="gwModalSubtitle">This also clears the device's tag source bindings.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ margin: 0, fontSize: 13, color: "var(--gw-text-secondary)" }}>
                Remove <strong>{deleteTarget.name}</strong>? Tags bound to this device will lose their Modbus source and be marked bad.
              </p>
			</div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing…" : "Remove device"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
